import { Fragment, ReactNode, useEffect, useState } from 'react';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHouse,
  faChevronRight,
  faAnglesRight,
  faPlus,
  faCrown,
} from '@fortawesome/pro-solid-svg-icons';
import BoxIcon from 'assets/icons/icon-box.svg';
import CollectionIcon from 'assets/icons/icon-collection.svg';
import useSpaces from 'networking/mindfeed/hooks/useSpaces';
import { useActiveSpace } from 'xstate/hooks/useActiveSpace';
import { useSpaceNavigation } from 'xstate/hooks/useSpaceNavigation';
import { useMemoryCreation } from 'hooks/useMemoryCreation';
import { closeSidebar } from 'xstate/actors/SidebarActor';
import { useSidebar } from 'xstate/hooks/useSidebar';
import IconButton from 'elements/IconButton';
import MemoryBoxList from 'modules/MemoryBox/MemoryBoxList';
import CollectionList from 'elements/Collection/CollectionList';
import { Sidebar, SidebarBody, SidebarHeader, SidebarSection } from './Sidebar';
import { useTranslations } from 'next-intl';

type Level = 'spaces' | 'boxes' | 'collections';

const BoxSidebar = () => {
  const t = useTranslations();
  const { selectedSpace, selectedBox, selectedCollection } = useActiveSpace();
  const { goToSpace } = useSpaceNavigation();
  const { createBox, createCollection, boxLocked, collectionLocked } =
    useMemoryCreation();
  const { data: spaces } = useSpaces();
  const { isOpen } = useSidebar();
  const [level, setLevel] = useState<Level>('collections');

  // Land on the collections level every time it opens, showing the collection
  // the user is currently in, rather than wherever they last browsed to.
  useEffect(() => {
    if (isOpen) setLevel('collections');
  }, [isOpen]);

  const handleCreateBox = () => {
    closeSidebar();
    createBox();
  };

  const handleCreateCollection = () => {
    closeSidebar();
    createCollection();
  };

  const crumbs: {
    level: Level;
    ariaLabel: string;
    icon: ReactNode;
    label: string;
  }[] = [
    {
      level: 'spaces',
      ariaLabel: t('spaces.short'),
      icon: <FontAwesomeIcon icon={faHouse} className={styles.crumbIcon} />,
      label: selectedSpace?.name ?? t('spaces.short'),
    },
    {
      level: 'boxes',
      ariaLabel: t('boxes.short'),
      icon: <BoxIcon className={styles.crumbIcon} />,
      label: selectedBox?.name ?? t('boxes.short'),
    },
    {
      level: 'collections',
      ariaLabel: t('collections.sectionLabel'),
      icon: <CollectionIcon className={styles.crumbIcon} />,
      label: selectedCollection?.name ?? t('collections.views.timeline.title'),
    },
  ];

  return (
    <Sidebar>
      <SidebarHeader className="border-none pb-0 pt-6">
        <div className={styles.titleRow}>
          <h1 className={styles.title}>{t('sidebar.goTo')}</h1>
          <button
            type="button"
            aria-label={t('sidebar.closeNavigation')}
            onClick={closeSidebar}
            className={styles.collapse}
          >
            <FontAwesomeIcon icon={faAnglesRight} className="text-lg" />
          </button>
        </div>
        <nav className={styles.breadcrumb}>
          {crumbs.map((crumb, index) => {
            const isActive = level === crumb.level;
            return (
              <Fragment key={crumb.level}>
                {index > 0 && (
                  <FontAwesomeIcon
                    icon={faChevronRight}
                    className={styles.separator}
                  />
                )}
                <button
                  type="button"
                  aria-label={crumb.ariaLabel}
                  onClick={() => setLevel(crumb.level)}
                  className={styles.crumb(isActive)}
                >
                  {crumb.icon}
                  <span className={styles.crumbLabel(isActive)}>
                    {crumb.label}
                  </span>
                </button>
              </Fragment>
            );
          })}
        </nav>
      </SidebarHeader>

      <SidebarBody className="pt-0">
        {level === 'spaces' && (
          <SidebarSection>
            <div className={styles.sectionHeader}>
              <p className={styles.sectionTitle}>{t('spaces.title')}</p>
            </div>
            {(spaces ?? []).map((space) => (
              <button
                key={space.id}
                type="button"
                onClick={() => goToSpace(space)}
                className={styles.row(space.id === selectedSpace?.id)}
              >
                <FontAwesomeIcon icon={faHouse} className={styles.rowIcon} />
                <span className={styles.rowLabel}>{space.name}</span>
              </button>
            ))}
          </SidebarSection>
        )}

        {level === 'boxes' && (
          <SidebarSection>
            <div className={styles.sectionHeader}>
              <p className={styles.sectionTitle}>{t('boxes.title')}</p>
              <IconButton
                icon={faPlus}
                ariaLabel={t('boxes.add')}
                onClick={handleCreateBox}
                badge={boxLocked ? { icon: faCrown } : undefined}
              />
            </div>
            <MemoryBoxList
              onBoxSelect={closeSidebar}
              onBoxEdit={closeSidebar}
            />
          </SidebarSection>
        )}

        {level === 'collections' && (
          <SidebarSection>
            <div className={styles.sectionHeader}>
              <p className={styles.sectionTitle}>
                {t('collections.sectionLabel')}
              </p>
              <IconButton
                icon={faPlus}
                ariaLabel={t('collections.add')}
                onClick={handleCreateCollection}
                badge={collectionLocked ? { icon: faCrown } : undefined}
              />
            </div>
            <CollectionList
              onCollectionSelect={closeSidebar}
              onCollectionEdit={closeSidebar}
              onCollectionDelete={closeSidebar}
            />
          </SidebarSection>
        )}
      </SidebarBody>
    </Sidebar>
  );
};

export default BoxSidebar;

const styles = {
  titleRow: 'flex items-center justify-between',
  // pl-2 aligns the text column with the row tiles (which sit at body pad + 8).
  title: 'text-xl font-bold text-ink-primary pl-2',
  collapse:
    'flex items-center justify-center h-9 w-9 rounded-full text-ink-primary hover:bg-ink-secondary/10',
  breadcrumb: 'flex items-center gap-1.5 mt-4 pl-1',
  separator: 'shrink-0 text-ink-tertiary text-xs',
  crumbIcon: 'w-4 h-4 shrink-0',
  // The label's max-width is what animates; the pill's auto width follows it
  // frame by frame, which a width transition on its own can't do.
  crumb: (isActive: boolean) =>
    classNames(
      'flex h-9 items-center justify-center overflow-hidden rounded-full text-sm',
      'transition-all duration-300 ease-out',
      isActive
        ? 'bg-accent-tint px-3 font-medium text-accent-tint-ink'
        : 'shrink-0 min-w-9 border border-stroke text-ink-secondary',
    ),
  crumbLabel: (isActive: boolean) =>
    classNames(
      'truncate transition-all duration-300 ease-out',
      isActive ? 'ml-2 max-w-40 opacity-100' : 'ml-0 max-w-0 opacity-0',
    ),
  sectionHeader: 'flex items-center justify-between mt-4 px-2 pb-2.5',
  sectionTitle: 'text-lg font-bold text-ink-primary',
  row: (isActive: boolean) =>
    classNames(
      'flex w-full flex-row items-center gap-3 rounded-[10px] border px-2 py-2.5 text-left',
      isActive
        ? 'border-surface-selection-border bg-surface-selection'
        : 'border-transparent hover:bg-ink-secondary/8',
    ),
  rowIcon: 'fa-fw w-5 text-ink-secondary',
  rowLabel: 'text-[15px] text-ink-primary',
};
